// Mock data for Wish Points prototype

export type Role = "admin" | "editor" | "viewer";

export interface User {
  id: string;
  nickname: string;
  email: string;
  role: Role;
  avatar?: string;
}

export interface Child {
  id: string;
  name: string;
  availablePoints: number;
  totalEarned: number;
  totalRedeemed: number;
  expiringSoon: number; // points expiring within 7 days
}

export type RecordKind = "scored" | "praise" | "reminder";

export interface WPRecord {
  id: string;
  kind: RecordKind;
  description: string;
  points?: number;
  category: string;
  tags?: string[];
  notes?: string;
  createdAt: string; // ISO
  createdBy: string;
  expiresAt?: string; // ISO
}

export type WishStatus = "redeemable" | "reached" | "fulfilled" | "cancelled";

export interface Wish {
  id: string;
  title: string;
  category: string;
  tags?: string[];
  required: number;
  budget?: string;
  constraints?: string;
  expected?: string;
  notes?: string;
  detail: string;
  status: WishStatus;
  fulfillments?: { at: string; note?: string }[];
}

export type LedgerKind = "earn" | "redeem" | "adjust" | "expire" | "restore";

export interface LedgerEntry {
  id: string;
  kind: LedgerKind;
  delta: number; // positive or negative
  source: string;
  createdAt: string;
  expiresAt?: string;
  recordId?: string;
}

// ---- fixtures ----

export const currentUser: User = {
  id: "u1",
  nickname: "妈妈",
  email: "mama@example.com",
  role: "admin",
};

export const child: Child = {
  id: "c1",
  name: "小满",
  availablePoints: 42,
  totalEarned: 128,
  totalRedeemed: 80,
  expiringSoon: 6,
};

const now = Date.now();
const day = 86400000;
const iso = (offsetDays: number) => new Date(now - offsetDays * day).toISOString();
const isoFuture = (offsetDays: number) => new Date(now + offsetDays * day).toISOString();

export const records: WPRecord[] = [
  {
    id: "r1",
    kind: "scored",
    description: "认真阅读了一本课外书,主动分享读后感",
    points: 3,
    category: "阅读",
    tags: ["主动"],
    createdAt: iso(0),
    createdBy: "妈妈",
    expiresAt: isoFuture(5),
  },
  {
    id: "r2",
    kind: "scored",
    description: "独立完成数学作业,字迹工整",
    points: 3,
    category: "作业",
    createdAt: iso(0),
    createdBy: "妈妈",
    expiresAt: isoFuture(30),
  },
  {
    id: "r3",
    kind: "praise",
    description: "帮妹妹整理玩具",
    category: "家务",
    createdAt: iso(0),
    createdBy: "爸爸",
  },
  {
    id: "r4",
    kind: "scored",
    description: "学校拿到好孩子卡",
    points: 5,
    category: "学校",
    createdAt: iso(1),
    createdBy: "妈妈",
    expiresAt: isoFuture(45),
  },
  {
    id: "r5",
    kind: "reminder",
    description: "写字姿势要坐直",
    category: "习惯",
    createdAt: iso(1),
    createdBy: "妈妈",
  },
  {
    id: "r6",
    kind: "scored",
    description: "主动倒垃圾",
    points: 4,
    category: "家务",
    createdAt: iso(2),
    createdBy: "爸爸",
    expiresAt: isoFuture(28),
  },
  {
    id: "r7",
    kind: "scored",
    description: "银铃卡奖励",
    points: 10,
    category: "学校",
    createdAt: iso(3),
    createdBy: "妈妈",
    expiresAt: isoFuture(60),
  },
  {
    id: "r8",
    kind: "praise",
    description: "对邻居阿姨很有礼貌",
    category: "礼仪",
    createdAt: iso(4),
    createdBy: "妈妈",
  },
  {
    id: "r9",
    kind: "scored",
    description: "练琴 20 分钟没有抱怨",
    points: 3,
    category: "练琴",
    createdAt: iso(5),
    createdBy: "妈妈",
    expiresAt: isoFuture(25),
  },
  {
    id: "r10",
    kind: "scored",
    description: "自己收拾书包",
    points: 2,
    category: "习惯",
    createdAt: iso(6),
    createdBy: "爸爸",
    expiresAt: isoFuture(20),
  },
];

export const wishes: Wish[] = [
  {
    id: "w1",
    title: "周末去科技馆",
    category: "外出",
    tags: ["家庭"],
    required: 30,
    budget: "¥120",
    expected: "本月内的周六",
    detail:
      "一起去市科技馆逛半天,可以自由挑一个展馆重点看。中午在馆内餐厅吃饭。回家前买一个纪念小物件。",
    status: "redeemable",
  },
  {
    id: "w2",
    title: "游戏时间 60 分钟",
    category: "娱乐",
    required: 20,
    detail:
      "游戏时长规则:\n- 每 10 点兑换 15 分钟,一次最多兑 60 分钟\n- 需在完成当日作业后使用\n- 不可在睡前 1 小时内使用",
    status: "redeemable",
  },
  {
    id: "w3",
    title: "新的乐高小套装",
    category: "礼物",
    required: 80,
    budget: "¥199 以内",
    constraints: "非节日限定",
    detail: "小满自己挑选,家长确认预算后购买。收到后由小满自己拼装完成。",
    status: "reached",
  },
];

export const ledger: LedgerEntry[] = [
  {
    id: "l1",
    kind: "earn",
    delta: 3,
    source: "认真阅读了一本课外书",
    createdAt: iso(0),
    expiresAt: isoFuture(5),
    recordId: "r1",
  },
  {
    id: "l2",
    kind: "earn",
    delta: 10,
    source: "银铃卡奖励",
    createdAt: iso(3),
    expiresAt: isoFuture(60),
    recordId: "r7",
  },
  {
    id: "l3",
    kind: "redeem",
    delta: -20,
    source: "兑换:游戏时间 30 分钟",
    createdAt: iso(4),
  },
];

export const templates = [
  { label: "认真阅读", points: 3, category: "阅读" },
  { label: "作业完成", points: 3, category: "作业" },
  { label: "做家务", points: 4, category: "家务" },
  { label: "好孩子卡", points: 5, category: "学校" },
  { label: "银铃卡", points: 10, category: "学校" },
  { label: "主动练琴", points: 3, category: "练琴" },
];

export function formatDate(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return `今天 ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  }
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

export function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / day);
}
